/**
 * Common date and time related constants and functions
 */
import appConfig from '../config/app-config';
import moment from 'moment';

const apiDateTimeFormat = 'YYYY-MM-DDTHH:mm:ss';
const apiDateFormat = 'YYYY-MM-DD';
const displayDateFormat = 'M/DD/YYYY';
const displayDateTimeSecondsFormat = `${displayDateFormat} HH:mm:ss`;
const displayDateTimeFormat = `${displayDateTimeSecondsFormat}.SSS`;

const displayDateRegex = /(0?[0-9]|1[0-2])([/\-.])([0-2]?[0-9]|3[01])([/\-.])([0-9]{4})/;

/**
 * European date & time formats
 */
const displayEuDateFormat = 'DD/MM/YYYY';
const displayEuDateTimeFormat = displayEuDateFormat + ' HH:mm:ss.SSS';
const inputEuDateTimeFormat = displayEuDateFormat + ' HH:mm:ss';

/**
 * DateTime format for date picker component
 *  TODO: make locale dependencies
 */
const inputDateFormat = 'MM/DD/YYYY';
const inputDateTimeFormat = inputDateFormat + ' HH:mm:ss';

/**
 * Common date and time representation formats
 */
export const dateFormats = {
    apiDateTimeFormat,
    displayDateFormat,
    displayDateTimeSecondsFormat,
    displayDateTimeFormat,
    inputDateTimeFormat,
    displayDateRegex,
    inputDateFormat,

    displayEuDateFormat,
    displayEuDateTimeFormat,
    inputEuDateTimeFormat
};

/**
 * TextMask component configurations
 */
export type InputMaskArray = Array<string | RegExp>;
export const inputDateMask: InputMaskArray = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
const inputTimeMask: InputMaskArray = [/[0-1]/, /\d/, ':', /[0-5]/, /\d/, ':', /[0-5]/, /\d/, ' ', /[aA]|[pP]/, 'M'];
export const inputDateTimeMask = [...inputDateMask, ' ', ...inputTimeMask];

/**
 * TextMask for European formats
 */
const inputEuTimeMask: InputMaskArray = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/, ':', /[0-5]/, /\d/];
export const inputEuDateTimeMask = [...inputDateMask, ' ', ...inputEuTimeMask];

/**
 * Formats the passed dateTime value to the API acceptable string
 * @param dateTime {moment.MomentInput}
 * @returns {string}
 */
export function toApiDateTime(dateTime: moment.MomentInput): string {
    const mDateTime = moment(dateTime);

    return mDateTime.utc().format(apiDateTimeFormat);
}

/**
 * Formats the passed date value to the API acceptable string
 * @param date {moment.MomentInput}
 * @param toUtc {boolean}
 * @returns {string}
 */
export function toApiDate(date: moment.MomentInput, {toUtc = true}: {toUtc?: boolean} = {}): string {
    const mDateTime = moment(date);

    if (toUtc) {
        mDateTime.utc();
    }

    return mDateTime.format(apiDateFormat);
}

/**
 * Compares two dates and returns true if they are equals
 * @param dateTime1 {moment.MomentInput}
 * @param dateTime2 {moment.MomentInput}
 * @returns {boolean}
 */
export function isDateTimesEquals(dateTime1: moment.MomentInput, dateTime2: moment.MomentInput): boolean {
    return (dateTime1 === dateTime2 || moment(dateTime1).isSame(dateTime2));
}

/**
 * Default start date time value.
 */
export function getDefaultStartDate(): Date {
    const [defaultDayStartTime] = appConfig.defaultDayTimeRange;

    return moment().startOf('day').add(defaultDayStartTime, 'milliseconds').utc().toDate();
}

/**
 * Default end date time value.
 */
export function getDefaultEndDate(): Date {
    const [, defaultDayEndTime] = appConfig.defaultDayTimeRange;

    return moment().startOf('day').add(defaultDayEndTime - 1, 'milliseconds').utc().toDate();
}
